import { useCallback, useEffect, useMemo } from 'react';
import { gql } from '@apollo/client';
import type { ApolloClient } from '@apollo/client';
import { useApolloClient, useQuery } from '@apollo/client/react';
import { buildAvatarMap, lookupAvatar, nextRefreshDelay, type UserAvatar } from './avatarMap';

export { buildAvatarMap, lookupAvatar, nextRefreshDelay, type UserAvatar } from './avatarMap';

/**
 * Everyone's profile photo, as ONE Apollo query shared by every `<Avatar>` on the page
 * (cache-first: a board of 200 cards is one request, not 200).
 *
 * Refresh: the signed URLs are minted for the hour and identical all hour, so one module-level
 * timer fires just after the earliest `expiresAt` (see `nextRefreshDelay`) and refetches
 * network-only; that write updates every watcher, which re-arms the timer from the new list.
 * Module-level because every mounted Avatar runs this hook — one timer, not one per face.
 */
export const USER_AVATARS = gql`
  query UserAvatars {
    userAvatars {
      email
      url
      expiresAt
    }
  }
`;

type AvatarsData = { userAvatars: UserAvatar[] };

let timer: ReturnType<typeof setTimeout> | null = null;
let timerFor: readonly UserAvatar[] | null = null;

/** Refetch `userAvatars` from the network (after an upload/remove, or when the URLs expire). */
export function refreshAvatars(client: ApolloClient): Promise<unknown> {
  return client.query<AvatarsData>({ query: USER_AVATARS, fetchPolicy: 'network-only' }).catch(() => undefined);
}

function schedule(client: ApolloClient, list: readonly UserAvatar[]) {
  if (timerFor === list) return; // already armed for this exact result
  if (timer) clearTimeout(timer);
  timerFor = list;
  timer = setTimeout(() => {
    timer = null;
    timerFor = null;
    void refreshAvatars(client);
  }, nextRefreshDelay(list, Date.now()));
}

export function useAvatars() {
  const client = useApolloClient();
  const { data } = useQuery<AvatarsData>(USER_AVATARS, { fetchPolicy: 'cache-first' });
  const list = data?.userAvatars;
  const map = useMemo(() => buildAvatarMap(list ?? []), [list]);

  useEffect(() => {
    if (list) schedule(client, list);
  }, [client, list]);

  const urlFor = useCallback((email: string | null | undefined) => lookupAvatar(map, email), [map]);
  const refetch = useCallback(() => refreshAvatars(client), [client]);
  return { map, urlFor, refetch };
}

/** One person's ready avatar URL, or null (→ initials). */
export function useAvatarUrl(email: string | null | undefined): string | null {
  const { map } = useAvatars();
  return lookupAvatar(map, email);
}
